<template>
  <v-card @click="() => emit('click')" class="rounded-lg bg-background">
    <v-row no-gutters align="center">
      <v-col cols="4" class="pl-2">
        <v-img
          :lazy-src="getSrc('car_images/placeholder_car.png')"
          :src="getImageSrc()"
          contain
          height="70"
        />
      </v-col>
      <v-col cols="8" style="min-height: 70px">
        <v-card-title class="d-flex justify-space-between text-subtitle-1 font-weight-medium">
          {{ vehicle.name ?? 'unnamed vehicle' }}
          <v-icon color="grey" size="small">mdi-dots-vertical</v-icon>
        </v-card-title>
        <v-card-subtitle class="font-weight-medium pr-12">
          {{ vehicle.licensePlate ?? 'unrecorded license plate' }}
        </v-card-subtitle>
      </v-col>
    </v-row>
    <v-card-actions>
      <div class="d-flex align-center pa-2 text-body-1">
        <v-icon size="20" color="pwt_azure" class="mr-1"> {{ fuelTypeIcon }} </v-icon>
        {{ vehicle.fuelType ? vehicle.fuelType : 'Electric' }}
      </div>
      <div v-if="hydrogen" class="d-flex align-center pa-2 text-body-1">
        <v-icon size="20" color="pwt_azure" class="mr-1"> mdi-meter-gas-outline </v-icon>
        {{ hydrogenTankSize }}kg tank
      </div>
      <div
        v-else-if="vehicle.advancedConfig?.BatterySize || vehicle.evModel"
        class="d-flex align-center pa-2 text-body-1"
      >
        <v-icon size="20" color="pwt_azure" class="mr-1"> mdi-battery-30 </v-icon>
        {{ vehicle.batterySize }}kW battery
      </div>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts" setup>
import Vehicle from '@/classes/vehicle_classes/vehicle'
import getAssetSrc from '@/utils/getAssetSrc'
import getDirectusImgUrl from '@/utils/getDirectusImgUrl'
import { batterySizeToTankSize, isHydrogen } from '@/utils/hydrogenUtils'
import { computed } from 'vue'

const { vehicle } = defineProps<{ vehicle: Vehicle }>()
const fuelTypeIcon = computed(() => {
  switch (vehicle.fuelType) {
    case 'Diesel':
    case 'Petrol':
      return 'custom:waterDrop'
    case 'Hybrid':
    case 'Plug in hybrid':
      return 'custom:waterEc'
    case 'Hydrogen':
      return 'custom:hydrogen'
    case 'Electric':
    default:
      return 'custom:offlineBolt'
  }
})
const emit = defineEmits(['click'])
function getImageSrc() {
  if (vehicle.imageSrcPaths.length) return vehicle.imageSrcPaths[0]
  if (vehicle.evModel?.imageUUID) return getDirectusImgUrl(vehicle.evModel.imageUUID)
  return getSrc('car_images/placeholder_car.png')
}

function getSrc(partialFilePath: string): string {
  return getAssetSrc(partialFilePath)
}

const hydrogen = computed(() => isHydrogen(vehicle))
const hydrogenTankSize = computed(() => Math.round(batterySizeToTankSize(vehicle.batterySize)))
</script>
